import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Map as MapIcon, Layers, AlertTriangle, Info, Route, FileText } from 'lucide-react';
import { Button } from '@common/Button';
import { LoadingSpinner } from '@common/LoadingSpinner';
import { Toast } from '@common/Toast';
import { OSRouteForm } from './OSRouteForm';
import { StructuresList } from './StructuresList';
import { StructureDetailsModal } from './StructureDetailsModal';
import { StructureIdsModal } from './StructureIdsModal';
import { calculateOSRoute } from '../../utils/osow/routeCalculator';
import { exportRouteToFormat } from '../../utils/osow/routeExporter';
import { logError, logInfo } from '@utils/logger';

// Google Maps API key
const GOOGLE_MAPS_API_KEY = 'AIzaSyAdNYCigSTxjhN3zL39QmB6H4nL5-zku7A';

export const OSRoutePlanner = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [routeData, setRouteData] = useState(null);
  const [structures, setStructures] = useState([]);
  const [displayMode, setDisplayMode] = useState('all'); // 'all', 'route', 'none'
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [showStructureDetails, setShowStructureDetails] = useState(false);
  const [showStructureIds, setShowStructureIds] = useState(false);
  
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const structureMarkersRef = useRef([]);

  // Load Google Maps API
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setMapLoaded(true);
      logInfo('Google Maps API loaded');
    };
    
    script.onerror = () => {
      setError('Failed to load Google Maps API');
      logError('Failed to load Google Maps API');
    };
    
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Initialize map when loaded
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    
    try {
      // Create map instance
      const mapOptions = {
        center: { lat: 45.5017, lng: -73.5673 }, // Montreal
        zoom: 10,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_RIGHT
        },
        fullscreenControl: false
      };
      
      googleMapRef.current = new google.maps.Map(mapRef.current, mapOptions);
      
      // Initialize directions renderer
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        map: googleMapRef.current,
        suppressMarkers: false,
        polylineOptions: {
          strokeColor: '#3B82F6',
          strokeWeight: 5,
          strokeOpacity: 0.8
        }
      });
      
      logInfo('Google Maps initialized successfully');
    } catch (error) {
      logError('Failed to initialize Google Maps:', error);
      setError('Failed to initialize map');
    }
  }, [mapLoaded]);

  // Handle route calculation
  const handleRouteCalculated = async (routeParams) => {
    setCalculating(true);
    setError(null);
    
    try {
      // Calculate route with clearance check
      const result = await calculateOSRoute(
        routeParams.origin,
        routeParams.destination,
        routeParams.height,
        routeParams.dimensions
      );
      
      setRouteData(result);
      setStructures(result.structures);
      
      // Display route on map
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections(result.directions);
      }
      
      // Display structures on map
      displayStructures(result.structures, 'route');
      
      // Hide form
      setShowForm(false);
      
      setToast({
        type: 'success',
        message: `Route calculated successfully with ${result.structures.length} structures`
      });
      
      logInfo('Route calculated successfully', result);
    } catch (error) {
      logError('Route calculation failed:', error);
      setError(error.message || 'Failed to calculate route');
      setToast({
        type: 'error',
        message: error.message || 'Failed to calculate route'
      });
    } finally {
      setCalculating(false);
    }
  };

  // Display structures on map
  const displayStructures = (structures, mode) => {
    // Clear existing markers
    structureMarkersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    structureMarkersRef.current = [];
    
    if (mode === 'none' || !structures?.length) return;
    
    // Create new markers
    structures.forEach(structure => {
      if (!structure.coordinates?.latitude || !structure.coordinates?.longitude) return;
      
      // Determine marker color based on clearance
      let markerColor = '#10B981'; // Green for safe
      
      if (routeData?.vehicleHeight) {
        const clearance = structure.properties?.clearance;
        const vehicleHeight = routeData.vehicleHeight;
        
        // vehicleHeight already includes safety buffer from OSRouteForm.jsx
        if (clearance && clearance < vehicleHeight) {
          markerColor = '#EF4444'; // Red for unsafe
        } else if (clearance && clearance < vehicleHeight + 0.5) {
          markerColor = '#F59E0B'; // Amber for caution
        }
      }
      
      const marker = new google.maps.Marker({
        position: {
          lat: Number(structure.coordinates.latitude),
          lng: Number(structure.coordinates.longitude)
        },
        map: googleMapRef.current,
        title: structure.name || `Structure ${structure.id}`,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: markerColor,
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          scale: 8
        }
      });
      
      // Add info window
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h3 style="font-weight: bold; margin-bottom: 4px;">${structure.name || 'Structure'}</h3>
            <p style="margin: 0;">Clearance: ${structure.properties?.clearance || 'Unknown'} m</p>
            <p style="margin: 0;">${structure.properties?.municipality || ''}</p>
          </div>
        `
      });
      
      marker.addListener('click', () => {
        infoWindow.open(googleMapRef.current, marker);
        setSelectedStructure(structure);
        setShowStructureDetails(true);
      });
      
      structureMarkersRef.current.push(marker);
    });
    
    // Update display mode
    setDisplayMode(mode);
  };

  // Handle export
  const handleExport = async (format) => {
    if (!routeData) return;
    
    try {
      const blob = await exportRouteToFormat(routeData, format);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `route-${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setToast({
        type: 'success',
        message: `Route exported as ${format.toUpperCase()}`
      });
      
      logInfo(`Route exported as ${format}`);
    } catch (error) {
      logError(`Failed to export route as ${format}:`, error);
      setToast({
        type: 'error',
        message: `Failed to export route as ${format}`
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      {/* Header */}
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-10 p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">OS/OW Route Planner</h1>
            <p className="text-sm text-gray-400">Plan safe routes for oversized/overweight vehicles</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)]">
        {/* Left Panel - Form & Results */}
        <div className="w-full lg:w-[30rem] bg-[#2A2B2E] p-4 overflow-y-auto">
          {showForm ? (
            <OSRouteForm
              onRouteCalculated={handleRouteCalculated}
              onCancel={() => navigate(-1)}
            />
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-white">Route Results</h2>
                <Button
                  onClick={() => setShowForm(true)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
                >
                  <Route className="w-4 h-4" />
                </Button>
              </div>
              
              {routeData && (
                <div className="bg-gray-700/50 p-4 rounded-lg">
                  <h3 className="text-white font-medium mb-2">Route Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Distance:</span>
                      <span className="text-white">{routeData.distance} km</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Duration:</span>
                      <span className="text-white">{routeData.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Vehicle Height:</span>
                      <span className="text-white">{routeData.vehicleHeight.toFixed(2)} m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Structures:</span>
                      <span className="text-white">{structures.length}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <h3 className="text-white font-medium mb-2">Export Options</h3>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => handleExport('kml')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={!routeData}
                    size="small"
                  >
                    KML
                  </Button>
                  <Button
                    onClick={() => handleExport('kmz')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={!routeData}
                    size="small"
                  >
                    KMZ
                  </Button>
                  <Button
                    onClick={() => handleExport('gpx')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={!routeData}
                    size="small"
                  >
                    GPX
                  </Button>
                </div>
              </div>
              
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <h3 className="text-white font-medium mb-2">Structure Display</h3>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => displayStructures(structures, 'all')}
                    className={`${displayMode === 'all' ? 'bg-blue-600' : 'bg-gray-600'} hover:bg-blue-700 text-white`}
                    disabled={!structures.length}
                    size="small"
                  >
                    All
                  </Button>
                  <Button
                    onClick={() => displayStructures(structures, 'route')}
                    className={`${displayMode === 'route' ? 'bg-blue-600' : 'bg-gray-600'} hover:bg-blue-700 text-white`}
                    disabled={!structures.length}
                    size="small"
                  >
                    Route
                  </Button>
                  <Button
                    onClick={() => displayStructures(structures, 'none')}
                    className={`${displayMode === 'none' ? 'bg-blue-600' : 'bg-gray-600'} hover:bg-blue-700 text-white`}
                    disabled={!structures.length}
                    size="small"
                  >
                    None
                  </Button>
                </div>
              </div>
              
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <h3 className="text-white font-medium mb-2">Structure References</h3>
                <Button
                  onClick={() => setShowStructureIds(true)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={!structures.length}
                  size="small"
                >
                  View Structure References
                </Button>
              </div>
              
              <StructuresList
                structures={structures}
                vehicleHeight={routeData?.vehicleHeight}
                onSelectStructure={(structure) => {
                  setSelectedStructure(structure);
                  setShowStructureDetails(true);
                  
                  // Center map on structure
                  if (googleMapRef.current && structure.coordinates) {
                    googleMapRef.current.setCenter({
                      lat: Number(structure.coordinates.latitude),
                      lng: Number(structure.coordinates.longitude)
                    });
                    googleMapRef.current.setZoom(16);
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Right Panel - Map */}
        <div className="flex-1">
          {loading || calculating ? (
            <div className="h-full flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <LoadingSpinner />
                <p className="text-white">{calculating ? 'Calculating route...' : 'Loading map...'}</p>
              </div>
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center p-4">
              <div className="bg-red-500/10 text-red-400 p-6 rounded-lg max-w-md text-center">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Error</h2>
                <p>{error}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="mt-4 bg-red-600 hover:bg-red-700 text-white"
                >
                  Reload
                </Button>
              </div>
            </div>
          ) : (
            <div 
              ref={mapRef} 
              className="w-full h-full"
              style={{ minHeight: 'calc(100vh - 64px)' }}
            ></div>
          )}
        </div>
      </div>

      {/* Structure Details Modal */}
      {selectedStructure && (
        <StructureDetailsModal
          isOpen={showStructureDetails}
          onClose={() => setShowStructureDetails(false)}
          structure={selectedStructure}
          vehicleHeight={routeData?.vehicleHeight}
        />
      )}

      {/* Structure IDs Modal */}
      <StructureIdsModal
        isOpen={showStructureIds}
        onClose={() => setShowStructureIds(false)}
        structures={structures}
      />

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Remove API Key display */}
      <style>{`
        .gm-style-cc {
          display: none !important;
        }
        .gmnoprint.gm-style-cc {
          display: none !important;
        }
        .gm-style div[style*="bottom: 0px"] {
          display: none !important;
        }
        div[style*="background-color: rgba(0, 0, 0, 0.5)"] {
          display: none !important;
        }
        div[style*="color: rgb(255, 255, 255)"] {
          display: none !important;
        }
        .gm-style-pbc {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default OSRoutePlanner;